import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { WithdrawalAdminService } from '../../_core/_services/withdrawal-admin.service';
import { NotificationService } from '../../_core/_services/notification.service';
import {
  WithdrawalAdminListItemDto,
  CompleteWithdrawalRequestDto,
  FailWithdrawalRequestDto
} from '../../_core/_model/withdrawal';

@Component({
  selector: 'app-withdrawal-requests',
  standalone: false,
  templateUrl: './withdrawal-requests.html',
  styleUrl: './withdrawal-requests.css'
})
export class WithdrawalRequests implements OnInit {
  loading = false;
  actionLoading = false;

  requests: WithdrawalAdminListItemDto[] = [];
  selectedRequest: WithdrawalAdminListItemDto | null = null;

  filterForm!: FormGroup;
  actionForm!: FormGroup;

  showCompleteModal = false;
  showFailModal = false;

  readonly statusOptions = ['Pending', 'Processing', 'Completed', 'Failed', 'Cancelled'];

  constructor(
    private fb: FormBuilder,
    private withdrawalAdminService: WithdrawalAdminService,
    private notifyService: NotificationService
  ) {}

  ngOnInit(): void {
    this.buildForms();
    this.loadRequests();
  }

  buildForms(): void {
    this.filterForm = this.fb.group({
      status: [''],
      currencyCode: [''],
      externalReference: [''],
      gatewayReference: [''],
      userId: ['']
    });

    this.actionForm = this.fb.group({
      gatewayReference: [''],
      providerResponseJson: [''],
      failureReason: ['']
    });
  }

  loadRequests(): void {
    this.loading = true;

    this.withdrawalAdminService.getWithdrawalRequests({
      status: this.toNullableString(this.filterForm.value.status),
      currencyCode: this.toNullableString(this.filterForm.value.currencyCode),
      externalReference: this.toNullableString(this.filterForm.value.externalReference),
      gatewayReference: this.toNullableString(this.filterForm.value.gatewayReference),
      userId: this.toNullableString(this.filterForm.value.userId)
    }).subscribe({
      next: (res) => {
        this.requests = res.data || [];
      },
      error: (err) => {
        this.notifyService.showError(
          err?.error?.Error?.Details || err?.error?.message || 'Failed to load withdrawal requests',
          'Error'
        );
        this.requests = [];
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  openCompleteModal(item: WithdrawalAdminListItemDto): void {
    this.selectedRequest = item;
    this.actionForm.reset({
      gatewayReference: item.gatewayReference || '',
      providerResponseJson: '',
      failureReason: ''
    });
    this.showCompleteModal = true;
  }

  openFailModal(item: WithdrawalAdminListItemDto): void {
    this.selectedRequest = item;
    this.actionForm.reset({
      gatewayReference: '',
      providerResponseJson: '',
      failureReason: item.failureReason || ''
    });
    this.showFailModal = true;
  }

  closeModals(): void {
    this.showCompleteModal = false;
    this.showFailModal = false;
    this.selectedRequest = null;
  }

  completeRequest(): void {
    if (!this.selectedRequest) return;

    this.actionLoading = true;

    const payload: CompleteWithdrawalRequestDto = {
      gatewayReference: this.toNullableString(this.actionForm.value.gatewayReference),
      providerResponseJson: this.toNullableString(this.actionForm.value.providerResponseJson)
    };

    this.withdrawalAdminService.completeWithdrawalRequest(this.selectedRequest.withdrawalId, payload).subscribe({
      next: () => {
        this.notifyService.showSuccess('Withdrawal marked as completed', 'Success');
        this.closeModals();
        this.loadRequests();
      },
      error: (err) => {
        this.notifyService.showError(
          err?.error?.Error?.Details || err?.error?.message || 'Failed to complete withdrawal',
          'Error'
        );
      },
      complete: () => {
        this.actionLoading = false;
      }
    });
  }

  failRequest(): void {
    if (!this.selectedRequest) return;

    this.actionLoading = true;

    const payload: FailWithdrawalRequestDto = {
      failureReason: this.toNullableString(this.actionForm.value.failureReason)
    };

    this.withdrawalAdminService.failWithdrawalRequest(this.selectedRequest.withdrawalId, payload).subscribe({
      next: () => {
        this.notifyService.showSuccess('Withdrawal marked as failed', 'Success');
        this.closeModals();
        this.loadRequests();
      },
      error: (err) => {
        this.notifyService.showError(
          err?.error?.Error?.Details || err?.error?.message || 'Failed to fail withdrawal',
          'Error'
        );
      },
      complete: () => {
        this.actionLoading = false;
      }
    });
  }

  retryRequest(item: WithdrawalAdminListItemDto): void {
    this.withdrawalAdminService.retryWithdrawalRequest(item.withdrawalId).subscribe({
      next: () => {
        this.notifyService.showSuccess('Withdrawal retry triggered', 'Success');
        this.loadRequests();
      },
      error: (err) => {
        this.notifyService.showError(
          err?.error?.Error?.Details || err?.error?.message || 'Failed to retry withdrawal',
          'Error'
        );
      }
    });
  }

  canComplete(item: WithdrawalAdminListItemDto): boolean {
    const status = (item.status || '').toLowerCase();
    return status === 'pending' || status === 'processing';
  }

  canFail(item: WithdrawalAdminListItemDto): boolean {
    const status = (item.status || '').toLowerCase();
    return status === 'pending' || status === 'processing';
  }

  canRetry(item: WithdrawalAdminListItemDto): boolean {
    return (item.status || '').toLowerCase() === 'failed';
  }

  badgeClass(status: string): string {
    switch ((status || '').toLowerCase()) {
      case 'completed':
      case 'confirmed':
        return 'bg-success-subtle text-success';
      case 'failed':
      case 'cancelled':
        return 'bg-danger-subtle text-danger';
      case 'processing':
      case 'pending':
        return 'bg-warning-subtle text-warning';
      default:
        return 'bg-secondary-subtle text-secondary';
    }
  }

  private toNullableString(value: any): string | null {
    const v = String(value ?? '').trim();
    return v ? v : null;
  }
}
